/**
 * Shareable calculator state in the query string, so a sales rep can send a
 * prefilled scenario and a visitor can bookmark one. Only values that differ
 * from the defaults are written, keeping links short.
 */

import { isCalculatorCurrency } from "./currencies";
import {
  amountMaxFor,
  clamp,
  OVERRIDE_MONTHS,
  PRICING_DEFAULTS,
  PRICING_RANGES,
  suggestedMonthlyCap,
  suggestedMonthlyRevenue,
} from "./defaults";
import { parseIsoDate, toIsoDate } from "./model";
import type { RepaymentStructure, SimulationParams } from "./model";

export interface CalculatorState extends SimulationParams {
  /** True once the visitor has set the cap themselves (stops auto-suggesting). */
  capTouched: boolean;
  /** True once the visitor has typed a revenue figure (stops auto-suggesting). */
  revenueTouched: boolean;
  /** ISO code the figures are shown in; preset from the request locale. */
  currency: string;
}

const KEYS = {
  structure: "structure",
  currency: "currency",
  amount: "amount",
  feePercent: "fee",
  startDate: "date",
  frequency: "frequency",
  termMonths: "term",
  revenueSharePercent: "share",
  monthlyRevenue: "revenue",
  monthlyGrowthPercent: "growth",
  cap: "cap",
  /** Set with `cap` when the visitor switched a cap they had set back off. */
  capOff: "capoff",
  overridePrefix: "m",
} as const;

const OVERRIDE_KEY = /^m\d+$/;

/**
 * Whether a query key belongs to the calculator. Everything else (UTMs,
 * click ids) is left untouched when the calculator rewrites the URL.
 */
export function isCalculatorParam(key: string): boolean {
  if (OVERRIDE_KEY.test(key)) return true;
  return (Object.values(KEYS) as string[]).includes(key);
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function readNumber(
  params: URLSearchParams,
  key: string,
  min: number,
  max: number
): number | undefined {
  const raw = params.get(key);
  if (raw === null || raw.trim() === "") return undefined;
  const value = Number(raw);
  if (!Number.isFinite(value)) return undefined;
  return clamp(value, min, max);
}

/**
 * The repayment structure a shared link sets, or null when the link sets none
 * or an unsupported value. Host pages fall back to their own preset on null.
 */
export function structureFromSearchParams(
  params: URLSearchParams
): RepaymentStructure | null {
  const raw = params.get(KEYS.structure);
  return raw === "cash-advance" || raw === "term-loan" ? raw : null;
}

/**
 * Builds state from the query string, falling back to defaults. A missing or
 * unsupported `currency` falls back to the domain's own.
 */
export function stateFromSearchParams(
  params: URLSearchParams,
  startDateFallback: string,
  defaultCurrency = "USD"
): CalculatorState {
  const currencyRaw = params.get(KEYS.currency)?.toUpperCase();
  const currency = isCalculatorCurrency(currencyRaw)
    ? currencyRaw
    : defaultCurrency;

  const structure =
    structureFromSearchParams(params) ?? PRICING_DEFAULTS.structure;

  const frequencyRaw = params.get(KEYS.frequency);
  const frequency =
    frequencyRaw === "daily" || frequencyRaw === "weekly"
      ? frequencyRaw
      : PRICING_DEFAULTS.frequency;

  const amount =
    readNumber(
      params,
      KEYS.amount,
      PRICING_RANGES.amount.min,
      amountMaxFor(currency)
    ) ?? PRICING_DEFAULTS.amount;
  const feePercent =
    readNumber(
      params,
      KEYS.feePercent,
      PRICING_RANGES.feePercent.min,
      PRICING_RANGES.feePercent.typedMax
    ) ?? PRICING_DEFAULTS.feePercent;

  const dateRaw = params.get(KEYS.startDate);
  const startDate =
    // Round-trip so an impossible date (2025-02-30) is rejected rather than
    // silently rolled into the next month.
    dateRaw &&
    ISO_DATE.test(dateRaw) &&
    !Number.isNaN(Date.parse(dateRaw)) &&
    toIsoDate(parseIsoDate(dateRaw)) === dateRaw
      ? dateRaw
      : startDateFallback;

  const revenueOverrides: Record<number, number> = {};
  for (let i = 0; i < OVERRIDE_MONTHS; i++) {
    const value = readNumber(
      params,
      `${KEYS.overridePrefix}${i + 1}`,
      PRICING_RANGES.revenueOverride.min,
      PRICING_RANGES.revenueOverride.max
    );
    if (value !== undefined) revenueOverrides[i] = value;
  }

  const revenueParam = readNumber(
    params,
    KEYS.monthlyRevenue,
    PRICING_RANGES.monthlyRevenue.min,
    PRICING_RANGES.monthlyRevenue.max
  );

  const capRaw = params.get(KEYS.cap);
  let capEnabled: boolean = PRICING_DEFAULTS.capEnabled;
  let capTouched = false;
  let monthlyCap = suggestedMonthlyCap(amount, feePercent);
  if (capRaw === "off") {
    capEnabled = false;
    capTouched = true;
  } else if (capRaw !== null) {
    const value = readNumber(
      params,
      KEYS.cap,
      PRICING_RANGES.monthlyCap.min,
      PRICING_RANGES.monthlyCap.max
    );
    if (value !== undefined) {
      capEnabled = params.get(KEYS.capOff) !== "1";
      capTouched = true;
      monthlyCap = value;
    }
  }

  return {
    structure,
    frequency,
    amount,
    feePercent,
    startDate,
    termMonths: Math.round(
      readNumber(
        params,
        KEYS.termMonths,
        PRICING_RANGES.termMonths.min,
        PRICING_RANGES.termMonths.max
      ) ?? PRICING_DEFAULTS.termMonths
    ),
    revenueSharePercent:
      readNumber(
        params,
        KEYS.revenueSharePercent,
        PRICING_RANGES.revenueSharePercent.typedMin,
        PRICING_RANGES.revenueSharePercent.typedMax
      ) ?? PRICING_DEFAULTS.revenueSharePercent,
    monthlyRevenue: revenueParam ?? suggestedMonthlyRevenue(amount),
    revenueTouched: revenueParam !== undefined,
    monthlyGrowthPercent:
      readNumber(
        params,
        KEYS.monthlyGrowthPercent,
        PRICING_RANGES.monthlyGrowthPercent.min,
        PRICING_RANGES.monthlyGrowthPercent.max
      ) ?? PRICING_DEFAULTS.monthlyGrowthPercent,
    revenueOverrides,
    capEnabled,
    monthlyCap,
    capTouched,
    currency,
  };
}

/** Serialises state to a query string containing only non-default values. */
export function searchParamsFromState(
  state: CalculatorState,
  todayIso: string,
  /** Currency the page infers on its own; omitted while nothing has changed. */
  domainCurrency?: string
): URLSearchParams {
  const params = new URLSearchParams();
  const set = (key: string, value: string | number) =>
    params.set(key, String(value));

  // Written first and filled in at the end, once we know whether anything
  // else changed.
  set(KEYS.currency, state.currency);

  if (state.structure !== PRICING_DEFAULTS.structure) {
    set(KEYS.structure, state.structure);
  }
  if (state.amount !== PRICING_DEFAULTS.amount) set(KEYS.amount, state.amount);
  if (state.feePercent !== PRICING_DEFAULTS.feePercent) {
    set(KEYS.feePercent, state.feePercent);
  }
  if (state.startDate !== todayIso) set(KEYS.startDate, state.startDate);
  if (state.frequency !== PRICING_DEFAULTS.frequency) {
    set(KEYS.frequency, state.frequency);
  }

  // Both structures' settings are written regardless of which is active, so a
  // rep can toggle between them on a call and the link still carries both.
  if (state.termMonths !== PRICING_DEFAULTS.termMonths) {
    set(KEYS.termMonths, state.termMonths);
  }
  {
    if (state.revenueSharePercent !== PRICING_DEFAULTS.revenueSharePercent) {
      set(KEYS.revenueSharePercent, state.revenueSharePercent);
    }
    if (state.revenueTouched) {
      set(KEYS.monthlyRevenue, state.monthlyRevenue);
    }
    if (state.monthlyGrowthPercent !== PRICING_DEFAULTS.monthlyGrowthPercent) {
      set(KEYS.monthlyGrowthPercent, state.monthlyGrowthPercent);
    }
    for (const [index, value] of Object.entries(state.revenueOverrides)) {
      set(`${KEYS.overridePrefix}${Number(index) + 1}`, value);
    }
    // A touched cap keeps its value even when switched off, so re-enabling
    // it from a shared link restores the same figure.
    if (state.capTouched) {
      set(KEYS.cap, state.monthlyCap);
      if (!state.capEnabled) set(KEYS.capOff, 1);
    }
  }

  // The copied link always carries the currency, so a shared scenario opens
  // in the sender's currency on any domain. The address bar passes the
  // domain's currency and leaves it out while the page is untouched; once
  // the visitor changes the currency or any figure, it is written too.
  const untouched = [...params.keys()].every((key) => key === KEYS.currency);
  if (state.currency === domainCurrency && untouched) {
    params.delete(KEYS.currency);
  }

  return params;
}
